import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertTriangle, PauseCircle, X, ShieldCheck, Calendar, CreditCard, Lock } from 'lucide-react';
import { apiClient } from '../api/client';

/**
 * Subscription Renewal Manager (Magic Link) — Sprint 197
 * ──────────────────────────────────────────────────────
 * Lands here from the 7-day-before-renewal email/SMS.
 *
 *  URL:  /subscription-renewal?token=…
 *  Flow:
 *    1. GET /api/subscription/renewal/preview → summary
 *    2. Tap Keep / Pause-90 / Cancel
 *    3. POST /api/subscription/renewal/action → applied via Stripe, token consumed
 */

const PURPLE = '#7B4FD8';
const ORANGE = '#F5831F';

const SubscriptionRenewal = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [phase, setPhase] = useState('loading');  // loading | invalid | ready | acting | done
  const [data, setData] = useState(null);
  const [actionResult, setActionResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setPhase('invalid');
      setError('Renewal link is missing its token.');
      return;
    }
    let cancelled = false;
    apiClient.request(`/api/subscription/renewal/preview?token=${encodeURIComponent(token)}`)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setPhase('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Renewal link no longer valid.');
        setPhase('invalid');
      });
    return () => { cancelled = true; };
  }, [token]);

  const act = async (action) => {
    setPhase('acting');
    setError(null);
    try {
      const res = await apiClient.request('/api/subscription/renewal/action', {
        method: 'POST',
        body: JSON.stringify({ token, action }),
      });
      setActionResult({ action, ...res });
      setPhase('done');
    } catch (err) {
      setError(err?.message || 'Could not apply that change. Please try again.');
      setPhase('ready');
    }
  };

  const amount = data ? `${(data.currency || 'USD').toUpperCase()} ${((data.amount_pence || 0) / 100).toFixed(2)}` : '';
  const nextDate = data?.next_charge_at ? new Date(data.next_charge_at).toLocaleDateString() : '—';

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-orange-50 py-12 px-4">
      <div className="max-w-xl mx-auto bg-white rounded-3xl shadow-xl p-6 sm:p-10" data-testid="subscription-renewal-page">
        <img src="/hibff-logo.png" alt="HiBFF" className="h-12 mx-auto mb-6" />

        {phase === 'loading' && (
          <Center><Loader2 size={40} className="animate-spin mx-auto mb-3" style={{ color: PURPLE }} /><p className="text-gray-600 text-sm">Checking your subscription…</p></Center>
        )}

        {phase === 'invalid' && (
          <Result icon={<AlertTriangle size={28} style={{ color: '#B42318' }} />} iconBg="#FEF3F2" heading="Renewal link can&apos;t be used">{error}</Result>
        )}

        {phase === 'done' && actionResult && (
          <Result
            icon={
              actionResult.action === 'cancel' ? <X size={28} style={{ color: '#475467' }} /> :
              actionResult.action === 'pause_indefinite' ? <Lock size={28} style={{ color: PURPLE }} /> :
              actionResult.action === 'pause_90' ? <PauseCircle size={28} style={{ color: '#B54708' }} /> :
              <CheckCircle2 size={28} style={{ color: '#059669' }} />
            }
            iconBg={
              actionResult.action === 'cancel' ? '#F2F4F7' :
              actionResult.action === 'pause_indefinite' ? '#F5F3FF' :
              actionResult.action === 'pause_90' ? '#FFFAEB' :
              '#ECFDF5'
            }
            heading={
              actionResult.action === 'cancel' ? 'Cancellation scheduled' :
              actionResult.action === 'pause_indefinite' ? 'Your memories are safe.' :
              actionResult.action === 'pause_90' ? 'Paused for 90 days' :
              'All set — see you next month!'
            }
            testid="renewal-done"
          >
            {actionResult.action === 'cancel'
              ? 'Your plan stays active until the end of the current period. You can undo this anytime from your dashboard.'
              : actionResult.action === 'pause_indefinite'
                ? 'Your HiBFF account is safely archived. Trophies, conversations, journey — all preserved. Come back any time, even years from now. The account becomes read-only until you reactivate.'
                : actionResult.action === 'pause_90'
                  ? 'No charges for the next 90 days. Your account stays exactly as it is — just paused.'
                  : 'Your subscription will renew as planned. Thank you for staying with HiBFF.'}
          </Result>
        )}

        {(phase === 'ready' || phase === 'acting') && data && (
          <div data-testid="renewal-form">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-1">Your HiBFF renewal</h1>
            <p className="text-gray-600 text-sm mb-6">Choose what you&apos;d like to do — one tap, no login required.</p>

            <div className="rounded-2xl p-5 mb-5 space-y-3" style={{ background: 'linear-gradient(135deg, #FAF5FF 0%, #FFF7ED 100%)', border: '1px solid #EDE9FE' }}>
              <Row icon={<Calendar size={14} />} label="Next charge" value={nextDate} />
              <Row icon={<CreditCard size={14} />} label="Amount" value={amount} highlight />
              <Row icon={<ShieldCheck size={14} />} label="Bank statement" value={data.statement_descriptor} mono />
              <Row label="Plan" value={data.plan_name || data.plan_id || '—'} />
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: '#FEF3F2', color: '#B42318', border: '1px solid #FDA29B' }}>{error}</div>
            )}

            <div className="space-y-2.5">
              <ActionBtn
                onClick={() => act('keep')}
                disabled={phase === 'acting'}
                primary
                testid="renewal-keep-btn"
              >
                Keep my subscription
              </ActionBtn>
              <ActionBtn
                onClick={() => act('pause_90')}
                disabled={phase === 'acting'}
                testid="renewal-pause-btn"
              >
                <PauseCircle size={16} className="inline mr-2" />
                Pause for 90 days (cooling-off)
              </ActionBtn>
              <ActionBtn
                onClick={() => act('pause_indefinite')}
                disabled={phase === 'acting'}
                memoryLane
                testid="renewal-pause-indefinite-btn"
              >
                <Lock size={16} className="inline mr-2" />
                Keep my memories (archive indefinitely)
              </ActionBtn>
              <ActionBtn
                onClick={() => act('cancel')}
                disabled={phase === 'acting'}
                danger
                testid="renewal-cancel-btn"
              >
                <X size={16} className="inline mr-2" />
                Cancel at period end
              </ActionBtn>
            </div>

            <p className="text-[11px] text-gray-500 text-center mt-5">
              <strong>Pause-90</strong> = no charges for 90 days, account stays intact, auto-resumes.{' '}
              <strong>Keep my memories</strong> = read-only forever, trophies &amp; conversations preserved, no charges.{' '}
              <strong>Cancel</strong> = active until {nextDate}, then lapses.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const Center = ({ children }) => <div className="text-center py-10">{children}</div>;

const Row = ({ icon, label, value, mono, highlight }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-gray-500">
      {icon}{label}
    </div>
    <div className={`${mono ? 'font-mono' : ''} ${highlight ? 'text-lg font-bold text-gray-900' : 'text-sm font-semibold text-gray-800'}`}>
      {value}
    </div>
  </div>
);

const ActionBtn = ({ onClick, disabled, primary, danger, memoryLane, children, testid }) => {
  const style = primary
    ? { background: `linear-gradient(135deg, ${ORANGE}, #FF9F4A)`, color: '#fff', boxShadow: `0 4px 14px ${ORANGE}55` }
    : memoryLane
      ? { background: '#F5F3FF', color: PURPLE, border: `1px solid ${PURPLE}40` }
      : danger
        ? { background: '#FEF3F2', color: '#B42318', border: '1px solid #FDA29B' }
        : { background: '#F2F4F7', color: '#344054' };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full px-6 py-3.5 rounded-full font-bold text-sm disabled:opacity-60 transition-transform hover:scale-[1.01] active:scale-[0.99]"
      style={style}
      data-testid={testid}
    >
      {children}
    </button>
  );
};

const Result = ({ icon, iconBg, heading, children, testid }) => (
  <div className="text-center py-10" data-testid={testid}>
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: iconBg }}>
      {icon}
    </div>
    <h1 className="text-xl font-bold text-gray-900 mb-2" dangerouslySetInnerHTML={{ __html: heading }} />
    <p className="text-gray-600 max-w-md mx-auto text-sm">{children}</p>
  </div>
);

export default SubscriptionRenewal;
